import * as dashjs from "dashjs";
//#region src/dom/dash/media-tracks.ts
/**
* Mirrors the dash.js video representations of the active stream into the media element's `videoRenditions` list, and
* wires user selection back to `engine.setRepresentationForTypeById()`.
*
* DASH representations belong to an adaptation set rather than to a flat list, so they are hung off a single `'main'`
* video track — the one the renditions of the stream that is playing are read from.
*
* Requires the media-tracks mixin (track-list infrastructure) to be applied earlier in the chain so the host exposes
* `addVideoTrack`, `videoRenditions`, and friends.
*/
function DashMediaMediaTracksMixin(BaseClass) {
	class DashMediaMediaTracks extends BaseClass {
		#src = "";
		#isBitrateSwitchingOff = false;
		constructor(...args) {
			super(...args);
			const { engine } = this;
			if (!engine) return;
			engine.on(dashjs.MediaPlayer.events.STREAM_INITIALIZED, this.#onStreamInitialized);
			engine.on(dashjs.MediaPlayer.events.QUALITY_CHANGE_RENDERED, this.#onQualityChangeRendered);
			this.addEventListener("sourcechange", this.#onSourceChange);
			this.videoRenditions.addEventListener("change", this.#switchRendition);
		}
		destroy() {
			const { engine } = this;
			engine?.off(dashjs.MediaPlayer.events.STREAM_INITIALIZED, this.#onStreamInitialized);
			engine?.off(dashjs.MediaPlayer.events.QUALITY_CHANGE_RENDERED, this.#onQualityChangeRendered);
			this.removeEventListener("sourcechange", this.#onSourceChange);
			this.videoRenditions.removeEventListener("change", this.#switchRendition);
			this.#removeVideoTracks();
			super.destroy();
		}
		#onStreamInitialized = (event) => {
			const { engine } = this;
			if (!engine || event.error) return;
			this.#reset();
			const videoTrack = this.addVideoTrack("main");
			videoTrack.selected = true;
			for (const representation of engine.getRepresentationsByType("video")) {
				const rendition = videoTrack.addRendition("", representation.width || void 0, representation.height || void 0, representation.codecs ?? void 0, toBitrate(representation), representation.frameRate || void 0);
				rendition.id = representation.id;
			}
			this.#setActiveRendition(engine.getCurrentRepresentationForType("video")?.id);
		};
		#onQualityChangeRendered = (event) => {
			if (event.mediaType !== "video") return;
			this.#setActiveRendition(event.newRepresentation?.id);
		};
		#switchRendition = () => {
			const { engine } = this;
			if (!engine) return;
			const selected = [...this.videoRenditions].find((rendition) => rendition.selected);
			if (!selected?.id) {
				this.#restoreBitrateSwitching();
				return;
			}
			this.#isBitrateSwitchingOff = true;
			engine.updateSettings(autoSwitchBitrate(false));
			engine.setRepresentationForTypeById("video", selected.id, true);
		};
		#onSourceChange = () => {
			const srcChanged = this.src !== this.#src;
			this.#src = this.src;
			if (srcChanged) {
				this.#reset();
				return;
			}
			if (this.#isBitrateSwitchingOff && this.#isEngineBitrateSwitchingOn()) this.#switchRendition();
		};
		#isEngineBitrateSwitchingOn() {
			return this.engine?.getSettings().streaming?.abr?.autoSwitchBitrate?.video !== false;
		}
		#setActiveRendition(id) {
			for (const rendition of this.videoRenditions) rendition.active = id !== void 0 && rendition.id === id;
		}
		#reset() {
			this.#removeVideoTracks();
			this.#restoreBitrateSwitching();
		}
		#restoreBitrateSwitching() {
			if (!this.#isBitrateSwitchingOff) return;
			this.#isBitrateSwitchingOff = false;
			this.engine?.updateSettings(autoSwitchBitrate(true));
		}
		#removeVideoTracks() {
			for (const videoTrack of this.videoTracks) this.removeVideoTrack(videoTrack);
		}
	}
	return DashMediaMediaTracks;
}
/**
* Dash.js plays a pinned representation only while its own bitrate switching is off, so selecting a rendition takes
* this setting alongside the representation.
*/
function autoSwitchBitrate(video) {
	return { streaming: { abr: { autoSwitchBitrate: { video } } } };
}
/**
* Representation bitrate in bits per second. `bandwidth` is what the manifest declares; the kbit reading is dash.js's
* own derived value, kept as a fallback for representations that carry one without the other.
*/
function toBitrate(representation) {
	const { bandwidth, bitrateInKbit } = representation;
	if (bandwidth) return bandwidth;
	return bitrateInKbit ? bitrateInKbit * 1e3 : void 0;
}
//#endregion
export { DashMediaMediaTracksMixin };

//# sourceMappingURL=media-tracks.js.map